import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import {
  DATE_FORMAT,
  DATE_FORMAT_WITH_FULL_DAY,
  MONTHS_PARAM,
  TEAM_TYPES
} from 'common/constants/constants';
import { MenuButtonItem } from 'components/MenuButton';
import { Flex, Separator } from 'components/base/layout-components';
import Text from 'components/base/Text';
import StyledAvatar from 'components/StyledAvatar';
import { SPACES } from 'common/constants/sizes';
import { isValidDate } from 'common/utils/time.utils';
import { createRemoveFromTeamModalOptions } from 'modals/modal-options-factory';
import { deleteFromAllTeams } from 'common/services/contributors.service';
import { ContributorManagementMenuItems } from 'components/ContributorManagementMenu/ContributorManagementMenu';
import { isTeamSynced } from 'state/selectors/teams';
import { filterAllContributors } from '../../ContributorsAndTeams.service';
import { LBRemoveUserIcon } from '@linearb/buzz';

const MAX_TEAMS_FOR_STRING_FORMAT = 3;

export const skeletonColumns = [
  'avatar-singleline',
  'singleline-random',
  'singleline-random',
  'singleline'
];

export const tableColumns = [
  {
    name: 'NAME',
    dataPoint: 'name'
  },
  {
    name: 'EMAIL',
    dataPoint: 'email'
  },
  {
    name: 'TEAMS',
    dataPoint: 'teams'
  },
  {
    name: 'ADDED',
    dataPoint: 'added'
  },
  {
    name: '',
    dataPoint: 'action',
    showOnlyOnHover: true
  }
];

const textParams = {
  size: 'large'
};

export const bulletParams = {
  backgroundColor: 'pigeonPost',
  width: '6px',
  height: '6px'
};

const formatTeamsName = (teams = [], isOnlyInAllContributors) => {
  const teamsFiltered = filterAllContributors(teams);
  const teamsJoined = teamsFiltered.map(({ name }) => name).join(', ');

  const teamsTableColumn = isOnlyInAllContributors ? (
    <Text color='darkRed' {...textParams} pl={SPACES.small}>
      Not in team
    </Text>
  ) : teamsFiltered.length < MAX_TEAMS_FOR_STRING_FORMAT ? (
    <Text {...textParams} multiLineEllipsisAfter={2}>
      {teamsJoined}
    </Text>
  ) : (
    <Tooltip placement='bottom-end' title={teamsJoined}>
      <Text {...textParams}>{`${teamsFiltered.length} teams`}</Text>
    </Tooltip>
  );

  const teamsString =
    teamsFiltered.length < MAX_TEAMS_FOR_STRING_FORMAT
      ? teamsJoined
      : `${teamsFiltered.length} teams`;

  return { teamsTableColumn, teamsString };
};

export const formattedTeamMembersForTable = (
  filteredTeamMembers,
  teamMembers,
  getTeams,
  licenseNextPaymentDate,
  toggleModal,
  toggleSnackBar,
  contributorsIds,
  isTeamLicense
) => {
  return filteredTeamMembers.map(
    ({ id, name, email, teams = [], added, extended_data }) => {
      const { avatar_url } = extended_data;

      const syncedTeams = teams.filter(isTeamSynced);
      const isSyncedContributor = syncedTeams.length > 0;
      const isOnlyInAllContributors =
        teams.length === 1 && teams[0]?.type === TEAM_TYPES.ALL_CONTRIBUTORS;

      const removeFromTeamMenuButton = (
        <MenuButtonItem
          key={'remove'}
          whiteSpace='nowrap'
          icon={<LBRemoveUserIcon />}
          text={'Remove this contributor from billing'}
          onClick={() =>
            toggleModal(
              createRemoveFromTeamModalOptions({
                teamMembers,
                id,
                name,
                teams,
                teamsNames: formatTeamsName(teams).teamsString,
                licenseNextPaymentDate,
                avatar_url,
                isSyncedContributor,
                onCancel: () => toggleModal(),
                onSubmit: onRemoveFromTeam({
                  getTeams,
                  toggleModal,
                  toggleSnackBar
                })
              })
            )
          }
        />
      );

      return {
        name: (
          <Flex direction='row' items='center'>
            <StyledAvatar
              avatar={{ url: avatar_url }}
              isSynced={isSyncedContributor}
            />
            <Text {...textParams} pl={SPACES.small}>
              {name}
            </Text>
          </Flex>
        ),
        email: <Text {...textParams}>{email}</Text>,
        teams: formatTeamsName(teams, isOnlyInAllContributors).teamsTableColumn,
        added: <Text {...textParams}>{moment(added).format(DATE_FORMAT)}</Text>,
        action: (
          <Flex justify='flex-end' items='center'>
            <ContributorManagementMenuItems
              currentContributor={contributorsIds[id]}
              moreMenuButtons={
                isTeamLicense || isOnlyInAllContributors
                  ? []
                  : [removeFromTeamMenuButton]
              }
            />
          </Flex>
        )
      };
    }
  );
};

const onRemoveFromTeam =
  ({ getTeams, toggleModal, toggleSnackBar = () => null }) =>
  async ({ id, teamsNames, contributorName }) => {
    let variant = 'success';
    let message = `You successfully removed ${contributorName} from ${teamsNames}`;

    try {
      await deleteFromAllTeams(id);
      await getTeams();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      variant = 'error';
      message = `Failed to remove ${contributorName}, please try again later`;
    }

    toggleModal();

    toggleSnackBar({
      message: (
        <Text size='large' color='white'>
          {message}
        </Text>
      ),
      variant
    });
  };

export const LICENSE_TYPE_KEYS = {
  paid: 'paid',
  trial: 'trial'
};

const commonLicenseTypeConfig = {
  upgradeTo: 'Enterprise',
  includes: ({
    maxRepos,
    reposLeft,
    metricHistoryValue,
    metricHistoryPeriod = ''
  }) => [
    <>
      <Text size='medium'>{maxRepos} repositories</Text>
      <Separator margin='0 1rem' />
      <Text size='medium' color='shuttleGray'>
        Currently scanning {maxRepos - reposLeft} Repos
      </Text>
    </>,
    // eslint-disable-next-line react/jsx-key
    <Text size='medium'>
      {metricHistoryValue} {metricHistoryPeriod.toLowerCase()} metrics history
    </Text>
  ]
};

export const LICENSE_TYPE_CONFIG = {
  [LICENSE_TYPE_KEYS.paid]: {
    upgradeContext: 'paid',
    name: 'Paid Plan',
    ...commonLicenseTypeConfig
  },
  [LICENSE_TYPE_KEYS.trial]: {
    upgradeContext: 'trial',
    name: 'Trial Plan',
    ...commonLicenseTypeConfig
  }
};

export const getNextPaymentDate = startDate => {
  const licenseStartDay = moment(startDate).date();
  const nextMonth = moment().add(1, MONTHS_PARAM).format('MM');
  const nextYear = moment().add(1, MONTHS_PARAM).format('YYYY');
  const nextPaymentDate = new Date(
    `${nextYear}-${nextMonth}-${licenseStartDay}`
  );

  // isValidDate(nextPaymentDate) = check if licenseStartDay exsists in next month. if not - go to last day next month
  return isValidDate(nextPaymentDate)
    ? moment(nextPaymentDate).format(DATE_FORMAT_WITH_FULL_DAY)
    : moment(`${nextYear}-${nextMonth}-01`)
        .endOf('month')
        .format(DATE_FORMAT_WITH_FULL_DAY);
};
